import { fileinfoIdEquals } from "./fileinfoIds";

/** What the agent can read. Mirrors the server's SUPPORTED_FILE_TYPES. */
export const SOURCE_EXTENSIONS = ["pdf", "xlsx", "csv"];

/** Human form of the above, for the copy that tells a user what to upload. */
export const SOURCE_EXTENSIONS_LABEL = "PDF, XLSX or CSV";

export function extensionOf(name) {
  return name && name.includes(".") ? name.split(".").pop().trim().toLowerCase() : "";
}

export function isExtractableFile(fileInfo) {
  return SOURCE_EXTENSIONS.includes(extensionOf(fileInfo?.filename || fileInfo?.title));
}

/**
 * The most recent extraction run for one source document.
 *
 * Matched with `fileinfoIdEquals`, never `===`. `run.sourceFileInfoId` is a Java `Long` and arrives as a JSON
 * number, while a question's `files` array round-trips as strings — so `===` matches nothing at all after a
 * reload, which looks exactly like the document never having been extracted.
 *
 * Runs arrive newest-first (`GET .../runs` orders by created date descending), so the first match is current.
 */
export function runForSource(runs, fileInfoId) {
  return (runs || []).find((run) => fileinfoIdEquals(run.sourceFileInfoId, fileInfoId)) || null;
}

export const SOURCE_STATE = {
  UNSUPPORTED: "UNSUPPORTED",
  RUNNING: "RUNNING",
  FAILED: "FAILED",
  EMPTY: "EMPTY",
  READY: "READY",
  UNSAVED: "UNSAVED",
  IDLE: "IDLE",
};

/**
 * What one source row should offer, given its file and its run.
 *
 * The ORDER of these checks is the design, not an implementation detail:
 *
 *  - UNSUPPORTED outranks everything, so a `.docx` reads as unreadable rather than as "save first" — the
 *    latter would send someone off to save a file that still could not be extracted afterwards.
 *  - Run states outrank UNSAVED, so a document that was removed and re-added still exposes the extraction it
 *    already has instead of hiding it behind a save it does not need.
 *  - CANCELLED collapses into IDLE. A cancelled run is not a result, and offering "Retry" rather than
 *    "Review" for one is a distinction with nothing behind it.
 *
 * @param {object} fileInfo
 * @param {object|null} run the file's most recent run, from `runForSource`
 * @param {{isUnsaved?: boolean}} context
 */
export function describeSourceRun(fileInfo, run, { isUnsaved = false } = {}) {
  const extension = extensionOf(fileInfo?.filename || fileInfo?.title);

  if (!SOURCE_EXTENSIONS.includes(extension)) {
    return {
      state: SOURCE_STATE.UNSUPPORTED,
      action: null,
      note: `Cannot extract from .${extension || "?"} files — use ${SOURCE_EXTENSIONS_LABEL}.`,
    };
  }

  if (run && ["QUEUED", "RUNNING"].includes(run.status)) {
    return { state: SOURCE_STATE.RUNNING, action: "cancel", note: null, run };
  }

  if (run && run.status === "FAILED") {
    return { state: SOURCE_STATE.FAILED, action: "retry", note: run.error || "Extraction failed.", run };
  }

  if (run && run.status === "DONE" && !run.rowsExtracted) {
    return {
      state: SOURCE_STATE.EMPTY,
      action: "extract",
      // Nothing kept but figures WERE read: the document is very likely for another reporting year. Said
      // outright, because "no metrics found" would send someone looking for a fault in a document that is
      // simply filed under the wrong year (ESG-4018).
      note: run.rowsOutOfPeriod
        ? `Every figure in this document (${run.rowsOutOfPeriod}) is for another period — is it filed under the right year?`
        : "No metrics found in this document.",
      run,
    };
  }

  if (run && run.status === "DONE") {
    return {
      state: SOURCE_STATE.READY,
      action: "review",
      note: [partialNote(run), outOfPeriodNote(run)].filter(Boolean).join(" ") || null,
      run,
    };
  }

  if (isUnsaved) {
    return {
      state: SOURCE_STATE.UNSAVED,
      action: null,
      note: "Save this page before reviewing.",
    };
  }

  return { state: SOURCE_STATE.IDLE, action: "extract", note: null, run };
}

/**
 * Says so when only part of a document was read.
 *
 * The one thing that distinguishes "there was nothing more to find" from "we did not finish reading it" — the
 * agent returns a confident partial answer either way.
 */
function partialNote(run) {
  if (run.unitsTotal == null || run.unitsProcessed == null) return null;
  if (run.unitsProcessed >= run.unitsTotal) return null;
  const parts = run.sourceFileType === "pdf" ? "pages" : "sheets";
  return `Only ${run.unitsProcessed} of ${run.unitsTotal} ${parts} were read, so some metrics may be missing.`;
}

/** Says how many figures were set aside as being for another reporting period (ESG-4018), when any were. */
function outOfPeriodNote(run) {
  const n = run.rowsOutOfPeriod;
  if (!n) return null;
  return `${n} figure${n === 1 ? " was" : "s were"} for another period and left out.`;
}

/** True while any run for this workstream is still open, which is what gates starting another. */
export function hasOpenRun(runs) {
  return (runs || []).some((run) => ["QUEUED", "RUNNING"].includes(run.status));
}
